import React, {ReactNode} from "react";
import ContentMode from "../ContentMode";
import Administration from "../session/Administration";
import {AbstractPanel, AbstractPanelProperties, AbstractPanelState} from "./AbstractPanel";
import Translate from "../../shared/translation/Translate";
import Helper from "../../shared/components/Helper";
import InfoBox, {InfoBoxType} from "../../shared/components/InfoBox";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import {
	Application,
	OAuthProvider,
	OAuthProviderName,
	OAuthProviders,
	PortalAuthTimeout,
	Server,
	SessionState,
	SessionTable,
	SessionTableEntry
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import DateHelper from "../../shared/helper/DateHelper";
import TimeUnit from "../../shared/units/TimeUnit";
import {Dropdown} from "primereact/dropdown";
import MenuButton from "../../shared/menu/MenuButton";
import FileHelper from "../../shared/helper/FileHelper";
import Timer from "../../shared/class/Timer";
import SortHelper from "../../shared/helper/SortHelper";
import Icon from "../../shared/components/Icon";
import azure from "../../../resources/images/oauth/azure.svg";
import auth0 from "../../../resources/images/oauth/auth0.svg";
import webpdf from "../../../resources/images/oauth/webpdf.png";
import {AdminDebugGlobals} from "../dialog/components/AdminDebugDialog";
import {Checkbox} from "primereact/checkbox";
import {Paginator} from "primereact/paginator";
import NumberHelper from "../../shared/helper/NumberHelper";
import {
	SessionAccessToken
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources/admin/config/server/SessionAccessToken";
import StorageItem from "../../shared/StorageItem";

enum SortType {
	NONE,
	STATUS,
	USER,
	ROLE,
	FILES,
	DISKSPACE,
	EXPIRY,
	IDLE,
	ID,
	PROVIDER
}

enum SortDirection {
	ASC,
	DESC
}

export interface SessionPanelProperties extends AbstractPanelProperties, AdminDebugGlobals {
}

export interface SessionPanelState extends AbstractPanelState {
	sortBy: SortType,
	sortDirection: SortDirection,
	selection: Array<string>
	currentPage: number;
}

export class SessionPanelDefinition extends AbstractPanel<SessionPanelProperties, SessionPanelState> {
	private updateTimer: Timer;
	private readonly ROW_SIZE: number = 15;

	constructor(props: Readonly<SessionPanelProperties>) {
		super(props);

		this.state = {
			...super.state,
			sortBy: SortType.NONE,
			sortDirection: SortDirection.ASC,
			selection: [],
			currentPage: 0
		};

		this.setSortBy = this.setSortBy.bind(this);

		this.updateTimer = new Timer(async () => {
			if (!FunctionHelper.exists(this.props.context.user)) {
				return;
			}

			let administration: Administration = this.props.context.session.getAdministration() as Administration;

			try {
				await administration.updateSessionTable();
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await administration.updateSessionTable();
				}
			}
		}, 5000, true);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let administration: Administration = this.props.context.session.getAdministration() as Administration;

		if (FunctionHelper.exists(this.props.context.user)) {
			try {
				await administration.updateSessionTable();
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await administration.updateSessionTable();
				}
			}
		}

		this.updateTimer.start();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		this.updateTimer.pause();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<SessionPanelProperties>, prevState: Readonly<SessionPanelState>, snapshot?: any
	): Promise<void> {
		if (this.props.contentMode !== prevProps.contentMode) {
			this.updateTimer.pause();

			if (this.props.contentMode === ContentMode.SESSIONS) {
				this.updateTimer.start();
			}
		}
	}

	private setSortBy(type: SortType): void {
		if (type === this.state.sortBy) {
			if (this.state.sortDirection === SortDirection.DESC) {
				this.setState({
					sortBy: SortType.NONE
				});
				return;
			}

			this.setState({
				sortDirection: SortDirection.DESC
			});
			return;
		}

		this.setState({
			sortBy: type,
			sortDirection: SortDirection.ASC
		});
	}

	private sortItems(listItems: Array<SessionTableEntry>): Array<SessionTableEntry> {
		let result: Array<SessionTableEntry> = listItems;
		let sessionTable: SessionTable | undefined = SessionTable.fromJson(this.props.sessionTable?.get());
		let serverConfig: Server | undefined = Server.fromJson(this.props.serverConfig?.get());
		let providers: Array<OAuthProvider> = FunctionHelper.applyValue(
			serverConfig?.oauth?.providers?.provider, OAuthProviders.getProviderDefault()
		);

		if (this.state.sortBy === SortType.NONE) {
			return result;
		}

		result = result.sort((a: SessionTableEntry, b: SessionTableEntry) => {
			switch (this.state.sortBy) {
				case SortType.STATUS:
					return SortHelper.alphanum(
						FunctionHelper.applyValue(a.sessionState, SessionTableEntry.getSessionStateDefault()),
						FunctionHelper.applyValue(b.sessionState, SessionTableEntry.getSessionStateDefault())
					);
				case SortType.USER:
					return SortHelper.alphanum(
						FunctionHelper.applyValue(a.userName, SessionTableEntry.getUserNameDefault()),
						FunctionHelper.applyValue(b.userName, SessionTableEntry.getUserNameDefault())
					);
				case SortType.ROLE:
					return a.isAdmin === true ? 1 : -1;
				case SortType.FILES:
					return SortHelper.numeric(
						FunctionHelper.applyValue(a.fileCount, SessionTableEntry.getFileCountDefault()),
						FunctionHelper.applyValue(b.fileCount, SessionTableEntry.getFileCountDefault())
					);
				case SortType.DISKSPACE:
					return SortHelper.numeric(
						FunctionHelper.applyValue(
							a.fileSize, SessionTableEntry.getFileSizeDefault()
						) + FunctionHelper.applyValue(
							a.fileSizeHistory, SessionTableEntry.getFileSizeHistoryDefault()
						),
						FunctionHelper.applyValue(
							b.fileSize, SessionTableEntry.getFileSizeDefault()
						) + FunctionHelper.applyValue(
							b.fileSizeHistory, SessionTableEntry.getFileSizeHistoryDefault()
						)
					)
				case SortType.EXPIRY:
					return SortHelper.numeric(
						FunctionHelper.applyValue(a.sessionExpiration, SessionTableEntry.getSessionExpirationDefault()),
						FunctionHelper.applyValue(b.sessionExpiration, SessionTableEntry.getSessionExpirationDefault())
					);
				case SortType.IDLE:
					return SortHelper.numeric(
						FunctionHelper.applyValue(
							sessionTable?.timestamp, SessionTable.getTimestampDefault()
						) -
						FunctionHelper.applyValue(
							a.lastAccess, SessionTableEntry.getLastAccessDefault()
						),
						FunctionHelper.applyValue(
							sessionTable?.timestamp, SessionTable.getTimestampDefault()
						) -
						FunctionHelper.applyValue(
							b.lastAccess, SessionTableEntry.getLastAccessDefault()
						)
					);
				case SortType.ID:
					return SortHelper.alphanum(
						FunctionHelper.applyValue(a.sessionId, SessionTableEntry.getSessionIdDefault()),
						FunctionHelper.applyValue(b.sessionId, SessionTableEntry.getSessionIdDefault())
					);
				case SortType.PROVIDER:
					let provider: OAuthProvider | undefined = providers.find((value: OAuthProvider) => {
						return value.id === a?.providerId;
					});
					let compareProvider: OAuthProvider | undefined = providers.find((value: OAuthProvider) => {
						return value.id === b?.providerId;
					});

					return SortHelper.alphanum(
						FunctionHelper.applyStringValue(provider?.displayName, provider?.name),
						FunctionHelper.applyStringValue(compareProvider?.displayName, compareProvider?.name)
					);
				default:
					return SortHelper.alphanum(
						FunctionHelper.applyValue(a.userName, SessionTableEntry.getUserNameDefault()),
						FunctionHelper.applyValue(b.userName, SessionTableEntry.getUserNameDefault())
					);
			}
		});

		if (this.state.sortDirection === SortDirection.DESC) {
			result = result.reverse();
		}

		return result;
	}

	private computeGradientColor(percentage: number, opacity: number): string {
		let red: number = 0;
		let green: number = 0;

		if (percentage < 0.5) {
			red = NumberHelper.parseInt(255 * percentage * 2);
			green = 255;
		} else {
			red = 255;
			green = NumberHelper.parseInt(255 - 255 * (percentage * 2 - 1));
		}

		return ["rgba(", red, ",", green, ",0,", opacity, ")"].join("");
	}

	private renderOptions(): ReactNode {
		return (
			<div className="functions">
				<div className="input-group">
					<label htmlFor="control-update">
						<Translate {...this.props} data="adminscreen.panels.session.general.update" namespace="screen"/>
					</label>
					<Dropdown value={this.updateTimer.getTime()} inputId="control-update"
							  onChange={(e) => {
								  this.updateTimer.setTime(e.value);
								  this.updateTimer.reset();
								  this.forceUpdate();
							  }}
							  appendTo={document.getElementById("root")}
							  options={[
								  {
									  label: DateHelper.formatTime(1000),
									  value: 1000
								  },
								  {
									  label: DateHelper.formatTime(5000),
									  value: 5000
								  },
								  {
									  label: DateHelper.formatTime(10000),
									  value: 10000
								  }
							  ]}/>
				</div>
			</div>
		);
	}

	private renderStatus(status: SessionState): ReactNode {
		switch (status) {
			case SessionState.Ok:
				return (
					<div className="status">
						<span className="icon icon-play tooltip" data-pr-position={"right"}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.session.list.status.ok", {ns: "screen"}
							  )}/>
					</div>
				);
			case SessionState.Expired:
				return (
					<div className="status">
						<span className="icon icon-pause tooltip" data-pr-position={"right"}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.session.list.status.expired",
								  {ns: "screen"}
							  )}/>
					</div>
				);
			case SessionState.Unknown:
				return (
					<div className="status">
						<span className="icon icon-warning tooltip" data-pr-position={"right"}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.session.list.status.unknown", {ns: "screen"}
							  )}/>
					</div>
				);
		}
	}

	public renderSortIcon(type: SortType): ReactNode {
		if (type !== this.state.sortBy) {
			return <span className="icon icon-sort-none"/>;
		}

		switch (this.state.sortDirection) {
			case SortDirection.ASC:
				return <span className="icon icon-sort-asc"/>;
			case SortDirection.DESC:
				return <span className="icon icon-sort-desc"/>;
		}
	}

	protected renderProviderIcon(providerId: string): ReactNode {
		let serverConfig: Server | undefined = Server.fromJson(this.props.serverConfig?.get());
		let providers: Array<OAuthProvider> = FunctionHelper.applyValue(
			serverConfig?.oauth?.providers?.provider, OAuthProviders.getProviderDefault()
		);
		let provider: OAuthProvider | undefined = providers.find((value: OAuthProvider) => {
			return value.id === providerId;
		});

		let providerLabel: string = FunctionHelper.applyStringValue(
			provider?.displayName, provider?.name,
			Translate.value("adminscreen.panels.session.list.provider.unknown", {ns: "screen"})
		);
		switch (provider?.name) {
			case OAuthProviderName.AzureAd:
				return <img src={azure} alt={providerLabel} className="tooltip provider-icon"
							data-pr-tooltip={providerLabel}/>;
			case OAuthProviderName.Auth0:
				return <img src={auth0} alt={providerLabel} className="tooltip provider-icon"
							data-pr-tooltip={providerLabel}/>;
			case OAuthProviderName.Generic:
				return <Icon icon={"icon-help provider-icon"} tooltip={providerLabel}/>;
		}

		return <img src={webpdf} alt="webPDF" className="tooltip provider-icon" data-pr-tooltip="webPDF"/>
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let applicationConfig: Application | undefined = Application.fromJson(this.props.applicationConfig?.get());
		let serverConfig: Server | undefined = Server.fromJson(this.props.serverConfig?.get());
		let sessionTable: SessionTable | undefined = SessionTable.fromJson(this.props.sessionTable?.get());
		let administration: Administration = this.props.context.session.getAdministration() as Administration;
		let entries: Array<SessionTableEntry> = this.sortItems(FunctionHelper.applyValue(
			sessionTable?.sessionList, SessionTable.getSessionListDefault()
		));
		let selection: Array<string> = this.state.selection;
		let pagedEntries: Array<SessionTableEntry> = entries.slice(
			this.state.currentPage, this.state.currentPage + this.ROW_SIZE
		);

		let allSelected: boolean = true;
		let tableItems: Array<ReactNode> = [];
		for (let entry of pagedEntries) {
			let idleSince: number =
				FunctionHelper.applyValue(sessionTable?.timestamp, SessionTable.getTimestampDefault()) -
				FunctionHelper.applyValue(entry.lastAccess, SessionTableEntry.getLastAccessDefault());
			let idleSinceFormatted: string = DateHelper.formatTime(idleSince);
			let inactivity: number = DateHelper.secondsToUnit(FunctionHelper.applyValue(
				applicationConfig?.portal?.auth?.timeout?.inactivity, PortalAuthTimeout.getInactivityDefault()
			), TimeUnit.MILLISECOND);
			let expiry: number = DateHelper.secondsToUnit(FunctionHelper.applyValue(
				entry.sessionExpiration, SessionTableEntry.getSessionExpirationDefault()
			), TimeUnit.MILLISECOND);
			let expiryFormatted: string = DateHelper.formatTime(expiry);
			let sessionExpiry: number = DateHelper.secondsToUnit(FunctionHelper.applyValue(
				serverConfig?.localAuth?.rest.accessToken?.expiration, SessionAccessToken.getExpirationDefault()
			), TimeUnit.MILLISECOND);

			if (
				entry.sessionId !== sessionTable?.sessionId && entry.sessionState === SessionState.Ok &&
				!selection.includes(FunctionHelper.applyValue(
					entry.sessionId, SessionTableEntry.getSessionIdDefault()
				))
			) {
				allSelected = false;
			}

			let isSelected: boolean = selection.includes(FunctionHelper.applyValue(
				entry.sessionId, SessionTableEntry.getSessionIdDefault()
			));

			tableItems.push(
				<tr className={entry.sessionId === sessionTable?.sessionId ? "marked" : ""}
					key={FunctionHelper.applyValue(entry.sessionId, SessionTableEntry.getSessionIdDefault())}>
					<td>
						{entry.sessionId !== sessionTable?.sessionId && entry.sessionState === SessionState.Ok &&
							<Checkbox checked={isSelected} onChange={() => {
								if (isSelected) {
									selection.splice(selection.indexOf(FunctionHelper.applyValue(
										entry.sessionId, SessionTableEntry.getSessionIdDefault()
									)), 1);
								} else {
									selection.push(FunctionHelper.applyValue(
										entry.sessionId, SessionTableEntry.getSessionIdDefault()
									));
								}

								this.setState({
									selection: selection
								}, this.forceUpdate);
							}}/>
						}
					</td>
					<td>
						{
							this.renderStatus(FunctionHelper.applyValue(
								entry.sessionState, SessionTableEntry.getSessionStateDefault()
							))
						}
					</td>
					<td>
						{
							FunctionHelper.applyValue(entry.userName, SessionTableEntry.getUserNameDefault())
						}
					</td>
					<td>
						{
							FunctionHelper.applyValue(
								entry.isAdmin, SessionTableEntry.getIsAdminDefault()
							) ?
								<Icon icon={"icon icon-user admin"} tooltip={
									<Translate {...this.props} data="adminscreen.panels.session.list.role.admin"
											   namespace="screen"/>
								}/> :
								<Icon icon={"icon icon-user"} tooltip={
									<Translate {...this.props} data="adminscreen.panels.session.list.role.user"
											   namespace="screen"/>
								}/>
						}
					</td>
					<td>
						<span className="badge tooltip" data-pr-autohide={false}
							  data-pr-tooltip={Translate.value(
								  "adminscreen.panels.session.list.files.tooltip", {
									  ns: "screen",
									  folder: FunctionHelper.applyValue(
										  entry.fileFolder, SessionTableEntry.getFileFolderDefault()
									  )
								  }
							  )} data-pr-position={"right"}>
							{
								FunctionHelper.applyValue(entry.fileCount, SessionTableEntry.getFileCountDefault())
							}
						</span>
					</td>
					<td>
						<span className="badge tooltip" data-pr-tooltip={Translate.value(
							"adminscreen.panels.session.list.diskspace.tooltip", {
								ns: "screen",
								files: FileHelper.formatFileSize(FunctionHelper.applyValue(
									entry.fileSize, SessionTableEntry.getFileSizeDefault()
								)),
								history: FileHelper.formatFileSize(FunctionHelper.applyValue(
									entry.fileSizeHistory, SessionTableEntry.getFileSizeHistoryDefault()
								))
							}
						)} data-pr-position={"right"}>
							{
								FileHelper.formatFileSize(
									FunctionHelper.applyValue(
										entry.fileSize, SessionTableEntry.getFileSizeDefault()
									) + FunctionHelper.applyValue(
										entry.fileSizeHistory, SessionTableEntry.getFileSizeHistoryDefault()
									)
								)
							}
						</span>
					</td>
					<td>
						{!FunctionHelper.isEmpty(expiryFormatted) &&
							<span className="badge" style={{
								background: this.computeGradientColor(
									1 - NumberHelper.limit(expiry / sessionExpiry, 0, 1),
									0.25
								)
							}}>
							{expiryFormatted}
						</span>
						}
					</td>
					<td>
						{!FunctionHelper.isEmpty(idleSinceFormatted) &&
							<span className="badge tooltip" style={{
								background: this.computeGradientColor(
									idleSince > inactivity ? 1 : 0, 0.25
								)
							}} data-pr-tooltip={idleSince > inactivity ? Translate.value(
								"adminscreen.panels.session.list.idle.inactive", {ns: "screen"}
							) : Translate.value(
								"adminscreen.panels.session.list.idle.active", {ns: "screen"}
							)}>
								{idleSinceFormatted}
							</span>
						}
					</td>
					<td>
						{
							this.renderProviderIcon(FunctionHelper.applyValue(
								entry.providerId, SessionTableEntry.getProviderIdDefault()
							))
						}
					</td>
					<td>
						{
							FunctionHelper.applyValue(entry.sessionId, SessionTableEntry.getSessionIdDefault())
						}
					</td>
					<td className="column-functions">
						{entry.sessionId !== sessionTable?.sessionId && entry.sessionState === SessionState.Ok &&
							<MenuButton {...this.props} icon="icon icon-close"
										tooltip={Translate.value(
											"adminscreen.panels.session.list.close.label", {ns: "screen"}
										)} tooltipOptions={{position: 'left'}}
										onClick={async () => {
											let closeSession: Function = async () => {
												await administration.closeSession(
													FunctionHelper.applyValue(
														entry.sessionId, SessionTableEntry.getSessionIdDefault()
													)
												);

												await administration.updateSessionTable();
											}

											let autoCloseSession: boolean = sessionStorage.getItem(
												StorageItem.SESSION_CLOSE
											) === "true";

											if (autoCloseSession) {
												try {
													await closeSession();
												} catch (exception: any) {
													if (await this.onError(exception)) {
														await closeSession();
													}
												}
											} else {
												this.props.context.getPage().setState({
													showConfirmDialog: true,
													confirmDialogDecision: StorageItem.SESSION_CLOSE,
													confirmDialogMessage: (
														<>
															<Translate {...this.props} namespace="screen"
																	   data="adminscreen.panels.session.list.close.confirm.single"
																	   values={{
																		   context: !FunctionHelper.isEmpty(FunctionHelper.applyValue(
																			   entry.userName, SessionTableEntry.getUserNameDefault()
																		   )) ? "user" : undefined,
																		   id: FunctionHelper.applyValue(
																			   entry.sessionId, SessionTableEntry.getSessionIdDefault()
																		   ),
																		   user: FunctionHelper.applyValue(
																			   entry.userName, SessionTableEntry.getUserNameDefault()
																		   )
																	   }}/>
														</>
													),
													confirmDialogSubmit: async () => {
														try {
															await closeSession();
														} catch (exception: any) {
															if (await this.onError(exception)) {
																await closeSession();
															}
														}
													}
												});
											}
										}}/>
						}
					</td>
				</tr>
			);
		}

		return (
			<div className="screen admin-session-panel" data-help="global.screen.admin.content" data-position="top">
				<div className="body">
					<h1>
						<Translate {...this.props} data={"adminscreen.panels.session.title"} namespace="screen"/>
						{this.renderHelpButton()}
					</h1>
					<Helper {...this.props}>
						<Translate {...this.props} data={"global.screen.admin.session"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						{this.renderError()}
					</fieldset>
					<fieldset className="p-grid">
						<InfoBox type={InfoBoxType.SUCCESS} label={
							Translate.value("adminscreen.panels.session.general.active", {ns: "screen"})
						}>
							{FunctionHelper.applyValue(
								sessionTable?.activeSessions, SessionTable.getActiveSessionsDefault()
							)}
						</InfoBox>
						<InfoBox type={InfoBoxType.DANGER} label={
							Translate.value("adminscreen.panels.session.general.expired", {ns: "screen"})
						}>
							{FunctionHelper.applyValue(
								sessionTable?.expiredSessions, SessionTable.getExpiredSessionsDefault()
							)}
						</InfoBox>
						<InfoBox type={InfoBoxType.INFO} label={
							Translate.value("adminscreen.panels.session.general.total", {ns: "screen"})
						}>
							{FunctionHelper.applyValue(
								sessionTable?.sessionCount, SessionTable.getSessionCountDefault()
							)}
						</InfoBox>
						<InfoBox className="diskspace" label={
							Translate.value("adminscreen.panels.session.general.diskspace", {ns: "screen"})
						}>
							{FileHelper.formatFileSize(FunctionHelper.applyValue(
								sessionTable?.sessionDiskUsage, SessionTable.getSessionDiskUsageDefault()
							), 2)}
						</InfoBox>
					</fieldset>
					{this.renderOptions()}
					<fieldset className="p-grid sessions">
						<div className="p-col-12 session-table">
							<table>
								<thead>
								<tr>
									<th className="select-all">
										<Checkbox checked={allSelected && selection.length > 0}
												  onChange={() => {
													  if (allSelected) {
														  this.setState({
															  selection: []
														  });
													  } else {
														  this.setState({
															  selection: entries.filter((value) => {
																  return value.sessionId !== sessionTable?.sessionId &&
																	  value.sessionState === SessionState.Ok;
															  }).map((value) => {
																  return FunctionHelper.applyValue(
																	  value.sessionId,
																	  SessionTableEntry.getSessionIdDefault()
																  );
															  })
														  });
													  }
												  }}/>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.STATUS)}>
										<div className="table-header">
											<Translate {...this.props} namespace="screen"
													   data="adminscreen.panels.session.list.status.label"/>
											{this.renderSortIcon(SortType.STATUS)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.USER)}>
										<div className="table-header">
											<Translate {...this.props} data="adminscreen.panels.session.list.user"
													   namespace="screen"/>
											{this.renderSortIcon(SortType.USER)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.ROLE)}>
										<div className="table-header">
											<Translate {...this.props} data="adminscreen.panels.session.list.role.label"
													   namespace="screen"/>
											{this.renderSortIcon(SortType.ROLE)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.FILES)}>
										<div className="table-header">
											<Translate {...this.props}
													   data="adminscreen.panels.session.list.files.label"
													   namespace="screen"/>
											{this.renderSortIcon(SortType.FILES)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.DISKSPACE)}>
										<div className="table-header">
											<Translate {...this.props} namespace="screen"
													   data="adminscreen.panels.session.list.diskspace.label"/>
											{this.renderSortIcon(SortType.DISKSPACE)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.EXPIRY)}>
										<div className="table-header">
											<Translate {...this.props} data="adminscreen.panels.session.list.expiry"
													   namespace="screen"/>
											{this.renderSortIcon(SortType.EXPIRY)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.IDLE)}>
										<div className="table-header">
											<Translate {...this.props} data="adminscreen.panels.session.list.idle.label"
													   namespace="screen"/>
											{this.renderSortIcon(SortType.IDLE)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.PROVIDER)}>
										<div className="table-header">
											<Translate {...this.props}
													   data="adminscreen.panels.session.list.provider.label"
													   namespace="screen"/>
											{this.renderSortIcon(SortType.PROVIDER)}
										</div>
									</th>
									<th className="sortable" onClick={() => this.setSortBy(SortType.ID)}>
										<div className="table-header">
											<Translate {...this.props} data="adminscreen.panels.session.list.id"
													   namespace="screen"/>
											{this.renderSortIcon(SortType.ID)}
										</div>
									</th>
									<th className="header-functions">
										<MenuButton {...this.props} icon="icon icon-close"
													disabled={selection.length === 0}
													tooltip={Translate.value(
														"adminscreen.panels.session.list.close.selected",
														{ns: "screen"}
													)} tooltipOptions={{position: 'left'}}
													onClick={async () => {
														let closeAllSessions: Function = async () => {
															for (let selectedId of selection) {
																if (!entries.find((value) => {
																	return value.sessionId === selectedId &&
																		value.sessionState === SessionState.Ok;
																})) {
																	// check if session is still in list and running
																	continue;
																}

																await administration.closeSession(selectedId);
															}

															await administration.updateSessionTable();
															this.setState({
																selection: []
															});
														};

														let autoCloseSession: boolean = sessionStorage.getItem(
															StorageItem.SESSION_CLOSE
														) === "true";

														if (autoCloseSession) {
															try {
																await closeAllSessions();
															} catch (exception: any) {
																if (await this.onError(exception)) {
																	await closeAllSessions();
																}
															}
														} else {
															this.props.context.getPage().setState({
																showConfirmDialog: true,
																confirmDialogDecision: StorageItem.SESSION_CLOSE,
																confirmDialogMessage: (
																	<Translate {...this.props} namespace="screen"
																			   data="adminscreen.panels.session.list.close.confirm.selection"/>
																),
																confirmDialogSubmit: async () => {
																	try {
																		await closeAllSessions();
																	} catch (exception: any) {
																		if (await this.onError(exception)) {
																			await closeAllSessions();
																		}
																	}
																}
															});
														}
													}}/>
									</th>
								</tr>
								</thead>
								<tbody>{tableItems}</tbody>
							</table>
						</div>
						<div className="p-col-12 session-footer">
							{entries.length > this.ROW_SIZE &&
								<Paginator totalRecords={entries.length} rows={this.ROW_SIZE}
										   first={this.state.currentPage}
										   onPageChange={(event) => this.setState({
											   currentPage: event.first
										   })}/>
							}
						</div>
					</fieldset>
				</div>
			</div>
		);
	}
}

const SessionPanel = React.memo(SessionPanelDefinition,
	(prevProps: Readonly<SessionPanelProperties>, nextProps: Readonly<SessionPanelProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (!prevProps.sessionTable?.equals(nextProps.sessionTable)) {
			return false;
		}

		if (!prevProps.applicationConfig?.equals(nextProps.applicationConfig)) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}

		return true;
	}
);

export default SessionPanel;